import type { LessonPreview } from '../types/lessonPreview.js';
import type {
  ActorType,
  BatchRunSummary,
  BulkApproveResult,
  MachineAuthority,
  QueueListResponse,
  QueueFilters,
  QueueStats,
  ReleaseWaveSummary,
  ReviewEventEntry,
  AnnotationAnchor,
  AnnotationView,
  WorkItemSummary,
} from '../types/workflow.js';
import {
  toBulkApproveOutcome,
  type BulkApproveGroupInput,
  type BulkApproveMode,
  type BulkApproveOutcome,
  type BulkApproveReport,
} from '../types/bulk-approve.js';
import type { TreeNode } from '../lib/treeDiff.js';
import { captureApiRequestError } from '../sentry.js';
import { writeSessionValue } from '../lib/browserStorage.js';
import { describeApiErrorBody } from '../lib/apiErrorMessage.js';
import type {
  ProgrammeLevelFilter,
  ProgrammeOverview,
  ProgrammeStatePublishResponse,
  ProgrammeStateResponse,
} from '../types/programme.js';

export interface SessionIdentity {
  userId: string;
  email: string;
  roles: string[];
  courses: string[];
  displayName: string;
}

export interface CourseSummary {
  id: string;
  title: string;
  shortTitle?: string;
  family?: string;
  /** CFA exam level, as declared by the API's catalog. Never derive this from `id` — `20b` is a
   *  Level I vintage (`cfa1-2024`) despite its shape, and `40b` is not a CFA course at all. */
  level?: 'Level I' | 'Level II' | 'Level III';
  curriculumYear?: number;
  preferredAuthoring?: boolean;
  supportsStructuralWorkflow?: boolean;
}

export interface GroupNode {
  id: number;
  parentId: number;
  title: string;
  children: GroupNode[];
}

export interface EntitySummary {
  id: number;
  title: string;
  published: boolean;
  item_type_slug: string | null;
  ordinal: number | null;
  group_id: number | null;
}

export interface EntityResponseGroup {
  id: number | null;
  options?: Record<string, unknown>;
  response_ids: number[];
  title?: string;
}

export interface EntityStep {
  id: number;
  prompt: string;
  value?: string;
  next_step_id: number | null;
  is_root?: boolean;
  is_question?: boolean;
  is_try_again?: boolean;
  response_format?: string;
  response_ids?: number[];
  response_groups?: EntityResponseGroup[];
  options?: Record<string, unknown>;
  video_url?: string | null;
  video_text?: string | null;
}

export interface EntityResponse {
  id: number;
  value: string;
  feedback?: string;
  is_correct: boolean | null;
  next_step_id: number | null;
  alt_values?: string[];
  type?: string;
}

export interface EntityDetail extends EntitySummary {
  summary?: string;
  rc_text?: string;
  options?: Record<string, unknown>;
  steps: EntityStep[];
  responses: EntityResponse[];
  step_counter?: number;
  response_counter?: number;
  group_id: number;
  _revisionHash: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AiProposalResponse {
  delta: Record<string, unknown>;
  message: string;
  document: EntityDetail;
  provider?: 'bedrock' | 'local_fallback';
}

export interface SaveEntityResponse {
  success: boolean;
  revisionId?: string;
  newRevisionHash?: string;
  validationErrors?: string[];
  conflict?: boolean;
  currentDocument?: Record<string, unknown>;
}

export interface RollbackReleaseWaveResponse {
  wave: ReleaseWaveSummary;
  rolledBackItemIds: string[];
}

export class AuthRedirectError extends Error {
  constructor() {
    super('Redirecting to sign-in');
  }
}

export class ApiRequestError extends Error {
  status: number | undefined;
  details: unknown;
  /** True when this message was invented by this client because the response explained nothing. */
  generic = false;

  constructor(message: string, status?: number, details?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.details = details;
  }
}

const CSRF_TTL_MS = 5 * 60 * 1000;
let cachedCsrfToken: string | null = null;
let csrfTokenFetchedAt = 0;

function redirectToLogin(): never {
  if (typeof window !== 'undefined') {
    window.location.assign('/auth/google');
  }

  throw new AuthRedirectError();
}

function isLocalDevHost() {
  if (typeof window === 'undefined') {
    return false;
  }
  const host = window.location.hostname.toLowerCase();
  return host === 'localhost' || host === '127.0.0.1';
}

async function tryLocalDevLogin() {
  if (!isLocalDevHost()) {
    return false;
  }

  const response = await fetch('/api/v1/auth/dev-login', {
    method: 'POST',
    credentials: 'include',
  });

  return response.ok;
}

export async function fetchSession({ redirectOnUnauthorized = true }: { redirectOnUnauthorized?: boolean } = {}) {
  if (redirectOnUnauthorized && isLocalDevHost()) {
    await tryLocalDevLogin();
  }

  const response = await fetch('/api/v1/auth/me', {
    credentials: 'include',
  });

  if (response.status === 401) {
    if (redirectOnUnauthorized && (await tryLocalDevLogin())) {
      const retried = await fetch('/api/v1/auth/me', {
        credentials: 'include',
      });
      if (retried.ok) {
        return (await retried.json()) as SessionIdentity;
      }
    }

    if (redirectOnUnauthorized) {
      return redirectToLogin();
    }
    return null;
  }

  if (!response.ok) {
    throw new Error('Unable to verify session');
  }

  return (await response.json()) as SessionIdentity;
}

export async function fetchCsrfToken() {
  const response = await fetch('/api/v1/auth/csrf-token', {
    credentials: 'include',
  });

  if (response.status === 401) {
    return redirectToLogin();
  }

  if (!response.ok) {
    throw new Error('Unable to load CSRF token');
  }

  return (await response.json()) as { token: string };
}

function invalidateCsrfToken() {
  cachedCsrfToken = null;
  csrfTokenFetchedAt = 0;
}

/**
 * Exported for `activity/signals.ts`, which posts a presence beacon and therefore needs the same
 * token every other mutation carries. It deliberately does NOT use `navigator.sendBeacon`: a beacon
 * cannot set headers, so it could not satisfy `CsrfGuard`, and exempting a write endpoint from CSRF
 * to buy a marginally more reliable flush is the wrong trade — a forged request would write false
 * presence data about a named colleague.
 */
export async function getCsrfToken() {
  const now = Date.now();
  if (cachedCsrfToken && now - csrfTokenFetchedAt < CSRF_TTL_MS) {
    return cachedCsrfToken;
  }

  const { token } = await fetchCsrfToken();
  cachedCsrfToken = token;
  csrfTokenFetchedAt = now;
  return token;
}

export async function logout() {
  const token = await getCsrfToken();
  const response = await fetch('/api/v1/auth/logout', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-CSRF-Token': token,
    },
  });

  if (!response.ok && response.status !== 401) {
    throw new Error('Logout failed');
  }

  invalidateCsrfToken();

  if (typeof window !== 'undefined') {
    // Guarded, because the NAVIGATION below is the part that matters. Written bare, a browser that
    // blocks site data threw here and never reached `location.assign`, so signing out did nothing at
    // all and the reviewer stayed on the page believing he had left it. The flag itself is only a
    // hint to `App` not to bounce straight back to Google; losing it costs one redirect.
    writeSessionValue('author.logged_out', 'true');
    // ...AND IN THE URL, because the line above is allowed to fail. In a browser that blocks site
    // data the marker is dropped silently, and without a second carrier the next load looks exactly
    // like an expired session: it redirects to Google, which still holds a session and is not asked
    // to prompt, and the reviewer who just signed out is signed straight back in. `App` accepts
    // either carrier.
    window.location.assign('/?signed_out=1');
  }
}

async function getJson<T>(path: string, options: { retryTransient?: boolean; signal?: AbortSignal } = {}) {
  const maxAttempts = options.retryTransient ? 3 : 1;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    const response = await fetch(path, {
      credentials: 'include',
      ...(options.signal ? { signal: options.signal } : {}),
    });

    if (response.status === 401) {
      return redirectToLogin();
    }

    if (response.ok) {
      return (await response.json()) as T;
    }

    if (options.retryTransient && attempt < maxAttempts && isTransientStatus(response.status)) {
      await delay(200 * attempt);
      continue;
    }

    captureApiRequestError({
      path,
      status: response.status,
      correlationId: response.headers.get('x-correlation-id'),
    });
    throw new ApiRequestError(buildRequestErrorMessage(path, response), response.status);
  }

  throw new ApiRequestError(`Request failed: ${path}`);
}

export function fetchCourses() {
  return getJson<CourseSummary[]>('/api/v1/courses');
}

export function fetchGroups(courseId: string) {
  return getJson<GroupNode[]>(`/api/v1/courses/${encodeURIComponent(courseId)}/groups`);
}

export function fetchStructureSubtree(courseId: string, groupId: number) {
  return getJson<TreeNode[]>(`/api/v1/courses/${encodeURIComponent(courseId)}/structure/${groupId}`);
}

export function fetchEntitiesByGroup(courseId: string, groupId: number) {
  return getJson<EntitySummary[]>(
    `/api/v1/courses/${encodeURIComponent(courseId)}/groups/${groupId}/entities`,
    { retryTransient: true },
  );
}

function isTransientStatus(status: number) {
  return status === 408 || status === 425 || status === 429 || status >= 500;
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildRequestErrorMessage(path: string, response: Response) {
  const correlationId = response.headers.get('x-correlation-id');
  const details = [`HTTP ${response.status}`];
  if (correlationId) {
    details.push(`correlation ${correlationId}`);
  }

  return `Request failed (${details.join(', ')}): ${path}`;
}

export function fetchEntity(courseId: string, entityId: number) {
  return getJson<EntityDetail>(`/api/v1/courses/${encodeURIComponent(courseId)}/entities/${entityId}`);
}

/**
 * A standalone practice question. It carries the same `{steps[], responses[]}` shape as a lesson
 * (`content.service.ts` `getTask` defaults both to `[]`), which is why the review row's answer-wiring
 * block can read either one through the same resolver. Task targets are the MAJORITY of the fix
 * backlog — 189 of 344 items across the three shipped fix runs — so this is not an edge case.
 */
export interface TaskDetail {
  id: number;
  title: string;
  /** Legacy task documents may omit these fields or carry malformed storage values at runtime. */
  published?: boolean | null;
  deleted?: boolean | null;
  snippet?: string;
  rc_text?: string;
  options?: Record<string, unknown>;
  steps: EntityStep[];
  responses: EntityResponse[];
  difficulty?: number;
  _revisionHash: string;
}

export function fetchTask(courseId: string, taskId: number) {
  return getJson<TaskDetail>(`/api/v1/courses/${encodeURIComponent(courseId)}/tasks/${taskId}`);
}

export interface EntityRef {
  id: number;
  title: string;
  group_id?: number | null;
}

/** Lessons that list this entity as a prerequisite (its dependents / downstream impact). */
export function fetchEntityDependents(courseId: string, entityId: number) {
  return getJson<EntityRef[]>(
    `/api/v1/courses/${encodeURIComponent(courseId)}/entities/${entityId}/dependents`,
  );
}

export interface PrerequisiteCandidate {
  id: number;
  title: string;
  /**
   * Brain2 builds the learner course from PUBLISHED lessons only and drops any published lesson
   * whose prerequisites are not themselves published — so an unpublished target is legal to write
   * and does not count. The editor marks it rather than hiding it; see `PrerequisiteEditor.tsx`.
   */
  published: boolean;
}

/** Live lessons an edge may point at. ARCHIVED lessons are excluded server-side, not here. */
export function fetchPrerequisiteCandidates(courseId: string, query: string, signal?: AbortSignal) {
  return getJson<PrerequisiteCandidate[]>(
    `/api/v1/courses/${encodeURIComponent(courseId)}/prerequisite-candidates?q=${encodeURIComponent(query)}`,
    signal ? { signal } : {},
  );
}

export async function sendChatMessage({
  courseId,
  entityId,
  instruction,
  currentDocument,
  chatHistory,
}: {
  courseId: string;
  entityId: number;
  instruction: string;
  currentDocument: EntityDetail;
  chatHistory: ChatMessage[];
}) {
  const token = await getCsrfToken();
  const response = await fetch('/api/v1/ai/chat', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
    },
    body: JSON.stringify({
      courseId,
      entityType: 'entity',
      entityId,
      instruction,
      currentDocument,
      chatHistory,
    }),
  });

  if (response.status === 401) {
    return redirectToLogin();
  }

  if (!response.ok) {
    throw new ApiRequestError('Unable to generate AI proposal', response.status);
  }

  return (await response.json()) as AiProposalResponse;
}

export async function saveEntity({
  courseId,
  entityId,
  document,
  baseRevisionHash,
  changeDescription,
}: {
  courseId: string;
  entityId: number;
  document: EntityDetail;
  baseRevisionHash: string;
  changeDescription: string;
}) {
  const token = await getCsrfToken();
  const response = await fetch(`/api/v1/courses/${encodeURIComponent(courseId)}/entities/${entityId}/save`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
    },
    body: JSON.stringify({
      document,
      baseRevisionHash,
      changeDescription,
    }),
  });

  if (response.status === 401) {
    return redirectToLogin();
  }

  if (response.status === 409) {
    const conflict = (await response.json()) as SaveEntityResponse;
    const error = new ApiRequestError('Save conflict', response.status);
    (error as Error & { conflict?: SaveEntityResponse }).conflict = conflict;
    throw error;
  }

  if (!response.ok) {
    throw new ApiRequestError('Unable to save entity', response.status);
  }

  return (await response.json()) as SaveEntityResponse;
}

function withQuery(path: string, params: Record<string, string | undefined>) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      query.set(key, value);
    }
  }
  const suffix = query.toString();
  return suffix ? `${path}?${suffix}` : path;
}

async function sendJson<T>(
  method: 'POST' | 'PUT',
  path: string,
  body: unknown,
  retryOnForbidden = true,
): Promise<T> {
  const token = await getCsrfToken();
  const response = await fetch(path, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': token,
    },
    body: JSON.stringify(body),
  });

  if (response.status === 401) {
    return redirectToLogin();
  }

  if (response.status === 403 && retryOnForbidden) {
    invalidateCsrfToken();
    return sendJson<T>(method, path, body, false);
  }

  if (!response.ok) {
    const responseText = await response.text();
    let details: unknown;
    try {
      details = responseText ? JSON.parse(responseText) : undefined;
    } catch {
      details = undefined;
    }
    // THE PER-FIELD ERRORS ARE THE PART THE REVIEWER NEEDS. This used to take the top-level
    // `message` alone, so a 400 carrying five lines naming the offending field arrived at three
    // separate approve screens as the bare headline "Invalid reviewerPatch". One chokepoint, three
    // callers: fixed here. A body with no structure still yields exactly the text it did before.
    const message = describeApiErrorBody(details, responseText);
    if (message) {
      throw new ApiRequestError(message, response.status, details);
    }
    // Nothing in the response explained anything. The message below is OURS, not the server's, so it
    // is marked as such: a screen with a better sentence for this situation should use its own.
    const generic = new ApiRequestError(`Request failed: ${path}`, response.status, details);
    generic.generic = true;
    throw generic;
  }

  return (await response.json()) as T;
}

async function postJson<T>(path: string, body: Record<string, unknown>, retryOnForbidden = true): Promise<T> {
  return sendJson<T>('POST', path, body, retryOnForbidden);
}

export async function putJson<T>(path: string, body: unknown, retryOnForbidden = true): Promise<T> {
  return sendJson<T>('PUT', path, body, retryOnForbidden);
}

export function fetchProgrammeOverview(level: ProgrammeLevelFilter, signal?: AbortSignal) {
  return getJson<ProgrammeOverview>(
    withQuery('/api/v1/programme/overview', { level }),
    signal ? { signal } : {},
  );
}

export function fetchProgrammeState() {
  return getJson<ProgrammeStateResponse>('/api/v1/programme/state');
}

export function putProgrammeState(document: unknown) {
  return putJson<ProgrammeStatePublishResponse>('/api/v1/programme/state', document);
}

export function fetchQueueItems(params: QueueFilters = {}, signal?: AbortSignal) {
  return getJson<QueueListResponse>(
    withQuery('/api/v1/queue/items', {
      status: params.status,
      courseKey: params.courseKey,
      sectionId: params.sectionId,
      runId: params.runId,
      priority: params.priority,
      waveId: params.waveId,
      cursor: params.cursor,
    }),
    signal ? { signal } : {},
  );
}

export function fetchLessonPreview(workItemId: string, signal?: AbortSignal): Promise<LessonPreview> {
  return getJson<LessonPreview>(`/api/v1/queue/items/${encodeURIComponent(workItemId)}/lesson-preview`, signal ? { signal } : {});
}

export function fetchWorkItem(id: string) {
  return getJson<WorkItemSummary>(`/api/v1/queue/items/${encodeURIComponent(id)}`);
}

export function fetchWorkItemHistory(id: string) {
  return fetchWorkItemHistoryPage(id).then((page) => page.events);
}

export function fetchWorkItemHistoryPage(id: string, cursor?: string) {
  return getJson<{ events: ReviewEventEntry[]; nextCursor?: string }>(
    withQuery(`/api/v1/queue/items/${encodeURIComponent(id)}/history`, { cursor }),
  );
}

export function fetchBatchRuns(courseKey?: string) {
  return getJson<BatchRunSummary[]>(
    withQuery('/api/v1/queue/runs', {
      courseKey,
    }),
  );
}

export function fetchBatchRun(runId: string) {
  return getJson<{ run: BatchRunSummary; items: WorkItemSummary[] }>(
    `/api/v1/queue/runs/${encodeURIComponent(runId)}`,
  );
}

export function fetchQueueStats(courseKey: string, signal?: AbortSignal) {
  return getJson<QueueStats>(
    `/api/v1/queue/stats?courseKey=${encodeURIComponent(courseKey)}`,
    signal ? { signal } : {},
  );
}

export function fetchReleaseWaves() {
  return getJson<ReleaseWaveSummary[]>('/api/v1/releases/waves');
}

export function fetchReleaseWaveDetail(waveId: string) {
  return getJson<ReleaseWaveSummary & { items?: WorkItemSummary[] }>(
    `/api/v1/releases/waves/${encodeURIComponent(waveId)}`,
  );
}

export function createReleaseWave(workItemIds: string[], courseKey: string) {
  return postJson<ReleaseWaveSummary>('/api/v1/releases/waves', { workItemIds, courseKey });
}

export function executeReleaseWave(waveId: string) {
  return postJson<{ executedItemIds: string[]; skippedItemIds: string[] }>(
    `/api/v1/releases/waves/${encodeURIComponent(waveId)}/execute`,
    {},
  );
}

export function rollbackReleaseWave(waveId: string) {
  return postJson<RollbackReleaseWaveResponse>(
    `/api/v1/releases/waves/${encodeURIComponent(waveId)}/rollback`,
    {},
  );
}

export function fetchAuditLog(limit = 100) {
  return getJson<AuditEvent[]>(`/api/v1/audit/recent?limit=${limit}`);
}

export interface AuditEvent {
  eventId: string;
  timestamp: string;
  action: string;
  userId: string;
  userEmail?: string;
  /** Absent ⇒ not declared machine. See `ActorType` in types/workflow.ts. */
  actorType?: ActorType;
  /** Present iff `actorType === 'machine'`. */
  actorAuthority?: MachineAuthority;
  courseKey: string;
  workItemId?: string;
  waveId?: string;
  details?: Record<string, unknown>;
}

/**
 * THE COURSE THE REVIEWER WAS LOOKING AT, sent with every decision.
 *
 * The server's `assertItemCourseAccess` proves the reviewer MAY touch the item's own course. It has
 * never proved it is the course they were LOOKING AT, and those two claims coincide only while a
 * reviewer holds exactly one grant. Once one person holds Level I, II and III, a decision taken on
 * one course's screen can land on another course's item with every guard passing.
 *
 * **The value must be the course the screen DISPLAYED — never `item.courseKey`.** Passing the
 * item's own course compares a value with itself and passes unconditionally: a correct call and a
 * wrong-course call would be byte-identical requests, which is the outcomes-indistinguishable shape.
 *
 * Optional here on purpose. This is stage one of the rollout: clients send it, the server refuses a
 * DISAGREEMENT and accepts an ABSENCE, so a tab left open across the deploy keeps working. Making it
 * mandatory is stage two and is an owner decision — see
 * `docs/execution/multi-level-authoring/PLAN.md` §9.2.
 */
export type ExpectedCourse = { expectedCourseKey?: string | null };

/** Spread into a decision body. Omits the key entirely when there is no displayed course, so an
 *  unknown course is an ABSENT field rather than a guessed one. */
function declaredCourse(expectedCourseKey?: string | null) {
  return expectedCourseKey ? { expectedCourseKey } : {};
}

export function approveItem(id: string, notes?: string, acknowledgeRenderIssues = false, expectedCourseKey?: string | null) {
  return postJson<WorkItemSummary>(`/api/v1/queue/items/${encodeURIComponent(id)}/approve`, {
    ...(notes ? { notes } : {}),
    ...(acknowledgeRenderIssues ? { acknowledgeRenderIssues: true } : {}),
    ...declaredCourse(expectedCourseKey),
  });
}

export function approveItemWithEdit(
  id: string,
  patch: Record<string, unknown>,
  notes?: string,
  acknowledgeRenderIssues = false,
  expectedCourseKey?: string | null,
) {
  return postJson<WorkItemSummary>(`/api/v1/queue/items/${encodeURIComponent(id)}/approve-with-edit`, {
    reviewerPatch: patch,
    ...(notes ? { notes } : {}),
    ...(acknowledgeRenderIssues ? { acknowledgeRenderIssues: true } : {}),
    ...declaredCourse(expectedCourseKey),
  });
}

export function cancelItem(id: string, notes?: string, expectedCourseKey?: string | null) {
  return postJson<WorkItemSummary>(`/api/v1/queue/items/${encodeURIComponent(id)}/cancel`, {
    ...(notes ? { notes } : {}),
    ...declaredCourse(expectedCourseKey),
  });
}

// Return a cancelled item to the queue (cancelled → pending). The non-approving recovery path.
export function restoreItem(id: string, notes?: string, expectedCourseKey?: string | null) {
  return postJson<WorkItemSummary>(`/api/v1/queue/items/${encodeURIComponent(id)}/restore`, {
    ...(notes ? { notes } : {}),
    ...declaredCourse(expectedCourseKey),
  });
}

export interface CommentResult {
  workItemId: string;
  timestamp: string;
  action: 'comment';
  postedToSlack: boolean;
}

// Attach a reviewer comment to an item (no status change). Persisted to the review history + audit
// trail and mirrored to the Slack decision-desk channel.
export function commentItem(id: string, text: string, expectedCourseKey?: string | null) {
  return postJson<CommentResult>(`/api/v1/queue/items/${encodeURIComponent(id)}/comment`, {
    text,
    ...declaredCourse(expectedCourseKey),
  });
}

export function fetchAnnotations(id: string) {
  return getJson<{ annotations: AnnotationView[] }>(`/api/v1/queue/items/${encodeURIComponent(id)}/annotations`);
}

export function createAnnotation(
  id: string,
  input: { annotationId: string; expectedPatchHash: string; anchor: AnnotationAnchor; comment: string },
) {
  return postJson<AnnotationView>(`/api/v1/queue/items/${encodeURIComponent(id)}/annotations`, input);
}

export function resolveAnnotation(id: string, annotationId: string) {
  return postJson<AnnotationView>(
    `/api/v1/queue/items/${encodeURIComponent(id)}/annotations/${encodeURIComponent(annotationId)}/resolve`,
    {},
  );
}

export function rateItem(id: string, ratings: Record<string, number>, note?: string) {
  return postJson<WorkItemSummary>(`/api/v1/queue/items/${encodeURIComponent(id)}/rating`, {
    ratings,
    ...(note ? { note } : {}),
  });
}

export function sourceDocumentHref(path: string, page?: number) {
  const base = `/api/v1/sources?path=${encodeURIComponent(path)}`;
  return page ? `${base}#page=${page}` : base;
}

export interface SourceSearchMatch {
  page: number;
  snippet: string;
}

export interface SourceSearchResult {
  status: 'ok' | 'image-only' | 'unavailable';
  matches: SourceSearchMatch[];
}

export function searchSourceDocument(path: string, query: string, signal?: AbortSignal) {
  const search = new URLSearchParams({ path, q: query });
  return getJson<SourceSearchResult>(
    `/api/v1/sources/search?${search.toString()}`,
    signal ? { signal } : {},
  );
}

/**
 * Approve declared ATOMIC GROUPS: sets of patches that must all apply or none. One unapprovable
 * member blocks its whole group server-side and no sibling is written.
 *
 * `mode: 'preflight'` writes nothing and reports what would happen — use it before a large run.
 * `machineActor` declares an automated decision under a named authority (AD-38); without it the run
 * is recorded as the operator's own reading of every item.
 */
export async function bulkApproveGroups(
  groups: BulkApproveGroupInput[],
  options: {
    mode?: BulkApproveMode;
    notes?: string;
    machineActor?: { decisionId: string; batchId: string; note?: string };
  } = {},
): Promise<BulkApproveOutcome> {
  const report = await postJson<BulkApproveReport>('/api/v1/queue/bulk-approve', {
    groups,
    // The server refuses a request with no `mode` on purpose, so that a raw script cannot apply by
    // accident. This client is an explicit caller, so it states its intent.
    mode: options.mode ?? 'apply',
    ...(options.notes ? { notes: options.notes } : {}),
    ...(options.machineActor ? { machineActor: options.machineActor } : {}),
  });
  return toBulkApproveOutcome(report);
}

export async function bulkCancelItems(ids: string[], notes?: string): Promise<BulkApproveResult> {
  return bulkSettle(ids, (id) => cancelItem(id, notes));
}

export async function bulkRestoreItems(ids: string[], notes?: string): Promise<BulkApproveResult> {
  return bulkSettle(ids, (id) => restoreItem(id, notes));
}

async function bulkSettle(
  ids: string[],
  action: (id: string) => Promise<WorkItemSummary>,
): Promise<BulkApproveResult> {
  const results = await Promise.allSettled(ids.map((id) => action(id)));
  return {
    succeeded: results.flatMap((result, index) =>
      result.status === 'fulfilled' ? [ids[index] ?? 'unknown'] : [],
    ),
    failed: results.flatMap((result, index) => {
      if (result.status !== 'rejected') {
        return [];
      }

      const reason = result.reason instanceof Error ? result.reason.message : String(result.reason);
      return [
        {
          workItemId: ids[index] ?? 'unknown',
          error: reason,
        },
      ];
    }),
  };
}
